import posthog from "posthog-js";

function readEnv(name: "VITE_POSTHOG_KEY" | "VITE_POSTHOG_HOST"): string | undefined {
  const value = import.meta.env[name] as string | undefined;
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * True when this page was served from a developer machine. Those loads
 * must not reach PostHog: the project token lives in local env, and the
 * dev server binds every interface, so a localhost or LAN session would
 * otherwise land in the production project.
 */
export function isLocalAnalyticsHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "");
  if (host === "localhost" || host.endsWith(".localhost")) return true;
  if (host === "::1" || host === "0.0.0.0") return true;
  if (host.endsWith(".local")) return true;
  return isPrivateIPv4(host);
}

function isPrivateIPv4(host: string): boolean {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!match) return false;
  const octets = match.slice(1).map(Number);
  if (octets.some((n) => n > 255)) return false;
  const [a, b] = octets;
  if (a === 127 || a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

function pageHostname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location.hostname;
}

const key = readEnv("VITE_POSTHOG_KEY");
const host = readEnv("VITE_POSTHOG_HOST");
const local = pageHostname() !== undefined && isLocalAnalyticsHost(pageHostname()!);

export const isPostHogConfigured = Boolean(key && host && !local);

/**
 * PostHog's app, not the ingestion proxy. A custom `api_host` is treated as
 * its own region, so the SDK would otherwise open the toolbar and recording
 * links on the proxy domain.
 */
const POSTHOG_UI_HOST = "https://us.posthog.com";

if (key && host && !local) {
  posthog.init(key, {
    api_host: host,
    ui_host: POSTHOG_UI_HOST,
    defaults: "2026-05-30",
    logs: {
      serviceName: "room-canvas-web",
      environment: import.meta.env.MODE,
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });
} else if (import.meta.env.DEV && !local) {
  const missingVariable = key ? "VITE_POSTHOG_HOST" : "VITE_POSTHOG_KEY";
  console.error(
    new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
    ),
  );
}

export default posthog;
